import { useState } from "react";
import DataTable, { ColumnType } from "../../../common/components/xepp/table/DataTable";
import { TablePagination } from "../../../common/components/xepp/table-pagination/TablePagination";
import EmptyState from "../../../common/components/xepp/empty-state/EmptyState";
import Button from "../../../common/components/xepp/button/Button";
import CommonLoadingSpinner from "../../../common/components/loading-spinner/CommonLoadingSpinnerForPage";
import { LockIcon, SearchIcon } from "../../../assets/icons";
import { Role } from "../../../model/roles/role";
import { useGetRolesQuery } from "../../../service/role-service";
import RolePermissionsSideDialog from "./RolePermissionsSideDialog";
import Can from "../../../guards/Can";
import { UtilityPermissions } from "../../../model/permissions/permissions";
import { FeatureFlagName } from "../../../tenant/features/featureFlags";
import { useFeatureFlag } from "../../../tenant/features/useFeatureFlag";
import { ADMIN_FEATURE_INFO } from "../adminFeatureCatalogue";
import styles from "./RoleManagement.module.scss";

const PAGE_SIZE = 10;

type DialogState = { mode: "create" | "edit"; roleId?: string };

const RoleManagementTab = () => {
	const [page, setPage] = useState(1);
	const [search, setSearch] = useState("");
	const [dialog, setDialog] = useState<DialogState | null>(null);
	const createAndAssignRolesOn = useFeatureFlag(FeatureFlagName.ADMIN_CREATE_AND_ASSIGN_ROLES);
	const editPermissionsOn = useFeatureFlag(FeatureFlagName.ADMIN_EDIT_PERMISSIONS);
	const createRoleLockDescription = ADMIN_FEATURE_INFO[FeatureFlagName.ADMIN_CREATE_AND_ASSIGN_ROLES]?.description;
	const editRoleLockDescription = ADMIN_FEATURE_INFO[FeatureFlagName.ADMIN_EDIT_PERMISSIONS]?.description;

	const { data, isLoading, isFetching, isError, refetch } = useGetRolesQuery({
		Page: page,
		PageSize: PAGE_SIZE,
		search: search || undefined,
	});

	const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setSearch(e.target.value);
		setPage(1);
	};

	const columns: Array<ColumnType<Role>> = [
		{
			accessor: "name",
			header: "Role",
			render: (record: Role) => <span>{record.name}</span>,
		},
		{
			accessor: "privileges",
			header: "Privileges",
			render: (record: Role) => {
				const subModules = Array.from(new Set(record.permissions.map(p => p.subModule).filter(Boolean)));
				return <span className={styles.privileges}>{subModules.join(", ") || "N/A"}</span>;
			},
		},
		{
			accessor: "action",
			header: "Action",
			render: (record: Role) =>
				record.isEditable ? (
					<Can permission={UtilityPermissions.EditRole}>
						{editPermissionsOn ? (
							<Button
								text="Edit"
								variant="outline"
								onClick={() => setDialog({ mode: "edit", roleId: record.id })}
							/>
						) : (
							<span title={editRoleLockDescription}>
								<Button text="Edit" variant="outline" disabled leftIcon={<LockIcon />} />
							</span>
						)}
					</Can>
				) : (
					<span className={styles.notEditable}>Not editable</span>
				),
		},
	];

	const headerActions = (
		<div className={styles.headerActions}>
			<SearchIcon />
			<input
				className={styles.searchInput}
				type="text"
				placeholder="Search by Name"
				value={search}
				onChange={handleSearchChange}
			/>
			<Can permission={UtilityPermissions.CreateRole}>
				{createAndAssignRolesOn ? (
					<Button text="Create New Role" className={styles.btn} onClick={() => setDialog({ mode: "create" })} />
				) : (
					<span title={createRoleLockDescription}>
						<Button text="Create New Role" className={styles.btn} disabled leftIcon={<LockIcon />} />
					</span>
				)}
			</Can>
		</div>
	);

	if ((isLoading || !data) && !isError) return <CommonLoadingSpinner loading={true}>{null}</CommonLoadingSpinner>;

	if (isError)
		return (
			<div className={styles.errorContainer}>
				<p className={styles.error}>Something went wrong while loading roles.</p>
				<Button text="Try again" onClick={refetch} />
			</div>
		);

	return (
		<>
			<DataTable<Role>
				data={data?.items ?? []}
				columns={columns}
				loading={isLoading}
				isUpdating={isFetching && !isLoading}
				headerActions={headerActions}
				emptyComponent={<EmptyState title="No roles found" />}
			/>

			<TablePagination
				isLoading={isFetching}
				totalItems={data?.count ?? 0}
				itemsPerPage={PAGE_SIZE}
				currentPage={page}
				onPageChange={setPage}
			/>

			{dialog && (
				<RolePermissionsSideDialog
					mode={dialog.mode}
					roleId={dialog.roleId}
					onClose={() => setDialog(null)}
				/>
			)}
		</>
	);
};

export default RoleManagementTab;
